/**
 * A profile photo, as something an `<img>` can actually render.
 *
 * ## Why this is not just a `src`
 *
 * The api Worker is on a different origin and authenticates with a bearer token in a header. An
 * `<img src="https://sign-api…/v1/profile/photo">` sends no `Authorization`, so it would get a 401
 * and every avatar in the console would fall back to initials forever. The browser has to fetch the
 * bytes itself, with the token, and hand the `<img>` an object URL instead.
 *
 * ## The lifecycle is the whole reason this is a hook
 *
 * `URL.createObjectURL` allocates until it is revoked. A member list that created one per row on
 * every render and never revoked would leak the whole list on every keystroke in a filter box. So
 * one URL per path, revoked when the path changes and when the component unmounts, and the fetch is
 * abandoned if either happens first.
 *
 * ## A 404 is an ordinary answer
 *
 * Most people have no photo, and `GET …/photo` says so with a 404. That resolves to `null`, the
 * caller passes `null` as `src`, and `Avatar` paints initials — which is what it did before any of
 * this existed. Only an unexpected failure is worth a console line, and even that is not worth a
 * banner: a missing picture is not something a person can act on.
 */
import { useEffect, useState } from 'react'
import { Avatar } from '@flowza/ui'
import { apiBlob, ApiError, apiConfigured } from './client'

/**
 * `path` is an API path such as `/v1/profile/photo`, or null to render nothing.
 *
 * `bust` changes when the photo has been replaced — the path is identical after an upload, so
 * without it the hook would have no reason to re-fetch and somebody would upload a new picture and
 * watch the old one stay on screen.
 */
export function usePhotoUrl(path: string | null, bust: unknown = null): string | null {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (path === null || !apiConfigured()) {
      setUrl(null)
      return
    }
    let objectUrl: string | null = null
    let cancelled = false
    const controller = new AbortController()

    void apiBlob(path, { accept: 'image/png,image/jpeg', signal: controller.signal })
      .then((blob) => {
        // Unmounted or re-pointed while the bytes were in flight: revoke immediately rather than
        // handing back a URL nobody will ever revoke.
        if (cancelled) return
        if (typeof URL.createObjectURL !== 'function') return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setUrl(null)
        // 404 is "this person has no photo", which is the common case and not a problem. Anything
        // else is worth one line for whoever is looking, and nothing louder: an avatar that fell
        // back to initials is not a failure a person can do anything about.
        if (error instanceof ApiError && error.status !== 404) {
          console.warn('profile photo could not be loaded', error.code)
        }
      })

    return () => {
      cancelled = true
      controller.abort()
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl)
    }
  }, [path, bust])

  return url
}

/**
 * One colleague's avatar, fetching their photo if they have one.
 *
 * A component rather than a `src` the caller computes, because the caller is a `.map()` over a
 * member list and a hook cannot be called inside one. One of these per row, each with its own
 * fetch, its own object URL and its own cleanup — which is also what makes a row leaving the list
 * release its picture.
 *
 * `memberId` is the `mem_…` id the member list already holds; the route accepts it with or without
 * the prefix. Null renders initials exactly as this did before there were photos.
 */
export function MemberAvatar({
  memberId,
  name,
  email,
  size = 'sm',
}: {
  readonly memberId: string | null
  readonly name?: string | null
  readonly email?: string | null
  readonly size?: 'sm' | 'md' | 'lg'
}) {
  const url = usePhotoUrl(
    memberId === null ? null : `/v1/organization/members/${encodeURIComponent(memberId)}/photo`,
  )
  return <Avatar name={name ?? null} email={email ?? null} src={url} size={size} />
}
